import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface BookingPayment {
  id: string;
  booking_id: string;
  amount_ore: number;
  paid_at: string;
  note: string | null;
  created_at: string;
  created_by: string | null;
}

export function useBookingPayments(bookingId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = ['booking-payments', bookingId];

  const { data: payments = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accommodation_booking_payments')
        .select('id, booking_id, amount_ore, paid_at, note, created_at, created_by')
        .eq('booking_id', bookingId!)
        .order('paid_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as BookingPayment[];
    },
    enabled: !!bookingId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['accommodation-booking-detail', bookingId] });
    queryClient.invalidateQueries({ queryKey: ['producer-accommodation-bookings-hub'] });
  };

  const addPayment = useMutation({
    mutationFn: async (params: {
      producerId: string;
      amountOre: number;
      paidAt?: string;
      note?: string;
    }) => {
      const { data, error } = await supabase.rpc('add_accommodation_booking_payment', {
        p_booking_id: bookingId!,
        p_producer_id: params.producerId,
        p_amount_ore: params.amountOre,
        p_paid_at: params.paidAt ?? new Date().toISOString(),
        p_note: params.note ?? null,
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      toast.success('Betalningen har registrerats');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message || 'Kunde inte registrera betalningen'),
  });

  const deletePayment = useMutation({
    mutationFn: async (params: { paymentId: string; producerId: string }) => {
      const { error } = await supabase.rpc('delete_accommodation_booking_payment', {
        p_payment_id: params.paymentId,
        p_booking_id: bookingId!,
        p_producer_id: params.producerId,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      toast.success('Betalningen har tagits bort');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message || 'Kunde inte ta bort betalningen'),
  });

  const totalPaidOre = payments.reduce((sum, p) => sum + p.amount_ore, 0);

  return {
    payments,
    isLoading,
    totalPaidOre,
    addPayment,
    deletePayment,
  };
}
